import { Time } from 'lightweight-charts'
import { ChartCandle } from '../chartV2/chart.typings';

export function getCumLineData(seriesData: ChartCandle[][]): Partial<ChartCandle>[] {
    const allTimes = new Set<Time>();
    seriesData.forEach(series => {
        series.forEach(point => allTimes.add(point.time));
    });

    const sortedTimes = Array.from(allTimes).sort();
    const seriesMaps = seriesData.map(series => {
        const map = new Map<Time, number>();
        series.forEach(point => {
            map.set(point.time, point.openInterest);
        });
        return map;
    });

    const previousValues = Array(seriesData.length).fill(0);
    const summedSeries: Partial<ChartCandle>[] = [];

    sortedTimes.forEach((time, i) => {
        let sum = 0;
        seriesMaps.forEach((seriesMap, index) => {
            if (seriesMap.has(time)) {
                previousValues[index] = seriesMap.get(time)!;
            }
            sum += previousValues[index];
        });
        summedSeries.push({
            time,
            openInterest: sum,
            series: i===0 ? 0 : sum - (summedSeries[summedSeries.length-1].openInterest??sum),
        });
    });

    return summedSeries;
}

/** Per-strike straddle: call close + put close, forward-filled on mismatched timestamps. */
export function getStraddleLineData(
    callData: ChartCandle[] | undefined,
    putData: ChartCandle[] | undefined
): Partial<ChartCandle>[] {
    const legs = [callData ?? [], putData ?? []]
    const allTimes = new Set<Time>()
    legs.forEach((series) => series.forEach((point) => allTimes.add(point.time)))
    const sortedTimes = Array.from(allTimes).sort((a, b) => Number(a) - Number(b))
    const maps = legs.map((series) => new Map(series.map((p) => [p.time, p] as const)))
    let prevCall: ChartCandle | undefined
    let prevPut: ChartCandle | undefined
    const result: Partial<ChartCandle>[] = []
    sortedTimes.forEach((time) => {
        const call = maps[0].get(time) ?? prevCall
        const put = maps[1].get(time) ?? prevPut
        if (!call || !put) return
        prevCall = call
        prevPut = put
        result.push({
            time,
            close: call.close + put.close,
            openInterest: (call.openInterest ?? 0) + (put.openInterest ?? 0),
        })
    })
    return result
}

function forwardFillSumLegs(
    legs: ChartCandle[][],
    field: 'close' | 'openInterest'
): Partial<ChartCandle>[] {
    const allTimes = new Set<Time>()
    legs.forEach((series) => series.forEach((point) => allTimes.add(point.time)))
    const sortedTimes = Array.from(allTimes).sort((a, b) => Number(a) - Number(b))
    const seriesMaps = legs.map((series) => {
        const map = new Map<Time, number>()
        series.forEach((point) => map.set(point.time, point[field] ?? 0))
        return map
    })
    const previousValues = Array(legs.length).fill(0)
    const summedSeries: Partial<ChartCandle>[] = []
    sortedTimes.forEach((time) => {
        let sum = 0
        seriesMaps.forEach((seriesMap, index) => {
            if (seriesMap.has(time)) {
                previousValues[index] = seriesMap.get(time)!
            }
            sum += previousValues[index]
        })
        summedSeries.push({ time, [field]: sum } as Partial<ChartCandle>)
    })
    return summedSeries
}

/** Total straddle premium across all selected strikes at each timestamp. */
export function getCumStraddleLineData(
    callLegs: ChartCandle[][],
    putLegs: ChartCandle[][]
): Partial<ChartCandle>[] {
    return forwardFillSumLegs([...callLegs, ...putLegs], 'close')
}

/** Per-strike PCR: put OI / call OI (forward-filled). Points with no call OI are skipped. */
export function getPcrLineData(
    callData: ChartCandle[] | undefined,
    putData: ChartCandle[] | undefined
): Partial<ChartCandle>[] {
    const legs = [callData ?? [], putData ?? []]
    const allTimes = new Set<Time>()
    legs.forEach((series) => series.forEach((point) => allTimes.add(point.time)))
    const sortedTimes = Array.from(allTimes).sort((a, b) => Number(a) - Number(b))
    const maps = legs.map((series) => new Map(series.map((p) => [p.time, p] as const)))
    let prevCall: ChartCandle | undefined
    let prevPut: ChartCandle | undefined
    const result: Partial<ChartCandle>[] = []
    sortedTimes.forEach((time) => {
        const call = maps[0].get(time) ?? prevCall
        const put = maps[1].get(time) ?? prevPut
        if (!call || !put) return
        prevCall = call
        prevPut = put
        const callOi = call.openInterest ?? 0
        const putOi = put.openInterest ?? 0
        if (callOi <= 0) return
        result.push({
            time,
            close: putOi / callOi,
        })
    })
    return result
}

/** Per-strike OI difference: call OI minus put OI (forward-filled). */
export function getOiDiffLineData(
    callData: ChartCandle[] | undefined,
    putData: ChartCandle[] | undefined
): Partial<ChartCandle>[] {
    const legs = [callData ?? [], putData ?? []]
    const allTimes = new Set<Time>()
    legs.forEach((series) => series.forEach((point) => allTimes.add(point.time)))
    const sortedTimes = Array.from(allTimes).sort((a, b) => Number(a) - Number(b))
    const maps = legs.map((series) => new Map(series.map((p) => [p.time, p] as const)))
    let prevCall: ChartCandle | undefined
    let prevPut: ChartCandle | undefined
    const result: Partial<ChartCandle>[] = []
    sortedTimes.forEach((time) => {
        const call = maps[0].get(time) ?? prevCall
        const put = maps[1].get(time) ?? prevPut
        if (!call || !put) return
        prevCall = call
        prevPut = put
        result.push({
            time,
            openInterest: (call.openInterest ?? 0) - (put.openInterest ?? 0),
            close: call.close,
        })
    })
    return result
}

/** Total call OI minus total put OI across all selected strikes. */
export function getCumOiDiffLineData(
    callLegs: ChartCandle[][],
    putLegs: ChartCandle[][]
): Partial<ChartCandle>[] {
    const callOi = forwardFillSumLegs(callLegs, 'openInterest')
    const putOi = forwardFillSumLegs(putLegs, 'openInterest')
    const callMap = new Map(callOi.map((c) => [c.time!, c.openInterest ?? 0] as const))
    const putMap = new Map(putOi.map((c) => [c.time!, c.openInterest ?? 0] as const))
    const times = Array.from(new Set([...callMap.keys(), ...putMap.keys()])).sort(
        (a, b) => Number(a) - Number(b)
    )
    const result: Partial<ChartCandle>[] = []
    times.forEach((time) => {
        const call = callMap.get(time)
        const put = putMap.get(time)
        if (call === undefined || put === undefined) return
        result.push({ time, openInterest: call - put, close: 0 })
    })
    return result
}

export function updateCumLineData(existingData: Partial<ChartCandle>[], seriesData: ChartCandle[][]): Partial<ChartCandle>[][] {
    if(!existingData || existingData.length === 0) {
        return [getCumLineData(seriesData), []]
    }
    let lastExistingTime = existingData[existingData.length-1].time
    if(!lastExistingTime) return [existingData, []]
    const newSeriesData: ChartCandle[][] = []
    seriesData.forEach((series)=>{
        let startIndex = series.length - 1
        while(startIndex>0 && series[startIndex].time>(lastExistingTime??0)) {
            startIndex--
        }
        startIndex = Math.max(0, startIndex-3)
        newSeriesData.push(series.slice(startIndex+1))
    })
    const prevData = [...existingData]
    prevData.pop()
    lastExistingTime = prevData[prevData.length-1].time
    let nextData = getCumLineData(newSeriesData)
    let i = 0
    while(i<nextData.length && nextData[i].time && (nextData[i]?.time??0)<=(lastExistingTime??0)) {
        i++
    }
    nextData = nextData.slice(i)

    return [[...prevData, ...nextData], nextData]
}